import configs from '../../configs';
import { getFromSessionStorage } from '../../utils/utils';

export default class MainService {
  user = null;
  controller = null;
  constructor() {
    this.user = getFromSessionStorage('user', {});
    this.controller = new AbortController();
  }

  fetchFun(url, method, body, header, options = {}) {
    const { signal: customSignal, skipSessionCheck = false } = options;

    // When user is in the process of logging out, skip all API requests
    // and return a dummy response. Do NOT reload the page — a reload would
    // cancel pending browser navigations (e.g., Keycloak logout redirect).
    // Version discovery is explicitly session-independent and must still run
    // after a page refresh, even if the logout marker is still present.
    if (
      sessionStorage.getItem('isLoggingOut') === 'true' &&
      !skipSessionCheck
    ) {
      return Promise.resolve({ results: [] });
    }

    const defaultSignal = this.controller.signal;
    const signal = customSignal || defaultSignal;

    const headers = header ?? new Headers();
    if (!header) {
      headers.append('Authorization', sessionStorage.getItem('java-token'));
      headers.append('Content-Type', 'application/json');
    }
    if (configs.Apim_Key) {
      headers.append('Ocp-Apim-Subscription-Key', configs.Apim_Key);
    }
    return new Promise((resolve, reject) => {
      fetch(url, {
        method,
        credentials: 'include',
        headers,
        body,
        signal,
      })
        .then(async res => {
          if (signal && signal.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
          }

          if (!skipSessionCheck) {
            this.checkSession(res);
          }

          const contentType = res.headers?.get?.('content-type') || '';
          const isJsonResponse = contentType.toLowerCase().includes('json');

          // API failures are sometimes replaced by an HTML error page by a
          // reverse proxy or an incorrectly configured API URL. Calling
          // res.json() unconditionally turns that useful response into the
          // unhelpful "Unexpected token '<'" error.
          let responseBody;
          if (typeof res.text === 'function' && !isJsonResponse) {
            responseBody = await res.text();
            try {
              responseBody = JSON.parse(responseBody);
            } catch {
              const preview = responseBody.trim().slice(0, 160);
              throw new Error(
                `Expected JSON from ${method.toUpperCase()} ${url}, received ${
                  contentType || 'non-JSON content'
                } (HTTP ${res.status || 'unknown'}): ${preview}`,
              );
            }
          } else {
            responseBody = await res.json();
          }

          if (res.ok === false) {
            const apiMessage =
              responseBody?.message || responseBody?.error?.message;
            const error = new Error(
              apiMessage ||
                `Request failed: ${method.toUpperCase()} ${url} (HTTP ${res.status})`,
            );
            error.status = res.status;
            error.response = responseBody;
            error.apiMessage = apiMessage;
            throw error;
          }

          resolve(responseBody);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  get(url, options = {}) {
    return this.fetchFun(url, 'get', null, null, options);
  }

  post(url, body, options = {}) {
    return this.fetchFun(url, 'post', JSON.stringify(body), null, options);
  }
  uploadFile(url, data, header) {
    if (header) {
      return this.fetchFun(url, 'post', data, header);
    }
    const headers = new Headers();
    headers.append('Authorization', sessionStorage.getItem('java-token'));
    return this.fetchFun(url, 'post', data, headers);
  }
  delete(url) {
    return this.fetchFun(url, 'delete');
  }
  put(url, body) {
    return this.fetchFun(url, 'PUT', JSON.stringify(body));
  }
  patch(url) {
    return this.fetchFun(url, 'PATCH');
  }
  checkSession(res) {
    if (res.status === 401 || res.code === 401) {
      sessionStorage.clear();
      this.controller.abort();
      window.location.reload(false);
    }
    return;
  }
}
